/**
 * Draws a PDF into a container with pdf.js (pdfjs-dist, Apache-2.0, pinned exactly in package.json): one section per
 * page, a "Page n of N" header, the page on a canvas, and pdf.js's TEXT LAYER over it, so the text can be selected,
 * searched and read by a screen reader or a script (View document, 2026-10-29; core's P2.0 reads the SpotOn report this
 * way). Loaded on the View screen only, so the app's own bundle does not carry it. The worker is a same-origin asset,
 * which the CSP's `script-src 'self'` already allows. Nothing is stored: the bytes live in memory for the page's life.
 *
 * ⚠ jsdom cannot draw a canvas, so this module is exercised in a real browser; the screen's tests replace it.
 */
export interface PdfLabels {
  pageOf: (page: number, pages: number) => string;
}

/** Renders every page into `container` (emptied first) and resolves to the page count. */
export async function renderPdf(container: HTMLElement, bytes: ArrayBuffer, labels: PdfLabels): Promise<number> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    import('pdfjs-dist/web/pdf_viewer.css'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;

  // A copy: pdf.js transfers the buffer it is given to its worker, and the caller's cached bytes must stay usable.
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)) }).promise;
  container.replaceChildren();
  const width = Math.max(320, container.clientWidth || 900);
  const ratio = window.devicePixelRatio || 1;

  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: Math.min(2, width / base.width) });

    const section = document.createElement('section');
    section.setAttribute('aria-label', labels.pageOf(n, doc.numPages));
    const header = document.createElement('h2');
    header.textContent = labels.pageOf(n, doc.numPages);
    header.style.cssText = 'font-size:14px;font-weight:600;margin:16px 0 8px;';

    const sheet = document.createElement('div');
    sheet.style.cssText = `position:relative;width:${viewport.width}px;height:${viewport.height}px;box-shadow:0 1px 4px rgba(0,0,0,.25);background:#fff;`;
    sheet.style.setProperty('--scale-factor', String(viewport.scale));
    sheet.style.setProperty('--user-unit', '1');
    sheet.style.setProperty('--total-scale-factor', String(viewport.scale));

    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.cssText = `width:${viewport.width}px;height:${viewport.height}px;display:block;`;
    canvas.setAttribute('aria-hidden', 'true');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas unavailable');

    const text = document.createElement('div');
    text.className = 'textLayer';

    sheet.append(canvas, text);
    section.append(header, sheet);
    container.append(section);

    await page.render({
      canvasContext: context,
      viewport,
      transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
    }).promise;
    await new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: text, viewport }).render();
  }
  return doc.numPages;
}
